import * as React from 'react';
import { Popover, Table } from 'antd';
import withStyles from 'react-jss';
import { connect } from 'react-redux';
import { formatNumber } from 'src/utils/utils';
import { renderAttachmentLink } from '../PDFViewComponent';

const styles = {
  noDataContainer: {
    textAlign: 'center',
    padding: '5px',
    width: 'fit-content',
    margin: 'auto',
  },
  popoverContent: {
    margin: '-12px -16px',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  headerContainer: {
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px',
    borderBottom: '1px solid #999999',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
  },
  headerTitle: {
    color: '#111111',
    fontSize: '12px',
    fontWeight: 600,
    margin: 0,
  },
  sectionContainer: {
    background: 'white',
    padding: 0,
  },
  table: {
    '& .ant-table-thead > tr > th': {
      backgroundColor: '#F2F7FC !important',
      padding: '12px 16px',
      fontSize: '14px',
      fontWeight: 600,
      color: '#000000',
      borderBottom: '1px solid #E0E0E0',
    },
    '& .ant-table-tbody > tr > td': {
      padding: '16px',
      fontSize: '14px',
      color: '#000000',
      borderBottom: '1px solid #F0F0F0',
    },
    '& .ant-table-tbody > tr:hover > td': {
      background: '#FAFAFA',
    },
    '& .ant-table-summary': {
      background: '#EBF4F2',
      fontWeight: 700,
      borderTop: '1px solid #E0E0E0',
    },
    '& .ant-table-summary > tr > td': {
      padding: '16px',
      fontSize: '14px',
      color: '#000000',
    },
  },
};

interface ReferenceDetailsProps {
  children: React.ReactNode;
  referenceData: {
    reference_number?: string;
    computation?: any;
    payout?: any[];
    payout_type?: string;
  };
  showCalculatedValue?: boolean;
  classes: Record<string, string>;
}

const payoutColumns = [
  {
    title: 'Charge Name',
    dataIndex: 'chargeName',
    key: 'chargeName',
    width: '30%',
    render: (text: string, record: any) => (
      <div>
        <div style={{ fontWeight: 500 }}>{text || record.chargeId || '-'}</div>
        {record.chargeType && (
          <div style={{ fontSize: '12px', color: '#666666', marginTop: '4px' }}>
            {record.chargeType}
          </div>
        )}
      </div>
    ),
  },
  {
    title: 'Amount',
    dataIndex: 'payout',
    key: 'payout',
    width: '20%',
    align: 'right' as const,
    render: (value: number) => {
      const isNegative = value < 0;
      return (
        <span style={{ color: isNegative ? '#FF0000' : '#000000' }}>
          {formatNumber(value)}
        </span>
      );
    },
  },
  {
    title: 'Attachment',
    dataIndex: 'attachment',
    key: 'attachment',
    width: '20%',
    align: 'center' as const,
    render: (url: string) => renderAttachmentLink(url),
  },
  {
    title: 'Remarks',
    dataIndex: 'remark',
    key: 'remark',
    width: '30%',
    render: (text: string) => text || '-',
  },
];

const ReferenceDetailsPopover: React.FC<ReferenceDetailsProps> = ({
  children,
  referenceData,
  showCalculatedValue,
  classes,
}) => {
  const hasValidData = referenceData?.payout?.length > 0;
  const headerTitle = !showCalculatedValue ? 'Adhoc Charges Breakup' : 'Charge Details';

  const content = (
    <div className={classes.popoverContent}>
      {!hasValidData ? (
        <div className={classes.noDataContainer}>No data available for this reference</div>
      ) : (
        <>
          <div className={classes.headerContainer}>
            <h4 className={classes.headerTitle}>{headerTitle}</h4>
          </div>
          <div className={classes.sectionContainer}>
            <div className={classes.table}>
              <Table
                dataSource={referenceData?.payout || []}
                columns={payoutColumns}
                pagination={false}
                size="small"
                scroll={{ y: 200 }}
                summary={(pageData) => {
                  const calculatedTotal =
                    (pageData || []).reduce((acc, curr) => acc + (curr.payout ?? 0), 0);
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                          <strong style={{ textTransform: 'uppercase' }}>TOTAL</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <strong>{formatNumber(calculatedTotal)}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} />
                        <Table.Summary.Cell index={3} />
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="hover"
      placement="right"
      overlayStyle={{
        width: hasValidData ? '748px' : 'fit-content',
        maxWidth: '90vw',
      }}
      overlayInnerStyle={{ padding: 0, borderRadius: '4px' }}
    >
      {children}
    </Popover>
  );
};

const mapStateToProps = (state: any) => ({
  showCalculatedValue: state.receivables?.payoutData?.show_calculated_value,
});

export default connect(mapStateToProps)(
  withStyles(styles, { injectTheme: true })(ReferenceDetailsPopover),
);
